import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import { Button, Chip, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import { reveniumApiRef } from '../../api/ReveniumApi';

// VIS-04 / D-12: keep spacing in makeStyles + theme tokens (no inline style props).
const useStyles = makeStyles((theme: Theme) => ({
  // Replaces the former inline marginLeft (8px) on the Request access Button.
  requestAccessButton: {
    marginLeft: theme.spacing(1), // 8px
  },
}));

export const EntityReveniumCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const reveniumApi = useApi(reveniumApiRef);
  const productId = entity.metadata.annotations?.['revenium.io/product-id'];
  const sourceId = entity.metadata.annotations?.['revenium.io/source-id'];
  const [state, setState] = useState<{
    loading: boolean;
    product?: any;
    subscribed?: boolean;
    error?: string;
  }>({ loading: true });

  useEffect(() => {
    if (!productId) {
      setState({ loading: false });
      return undefined;
    }
    let active = true;
    Promise.all([
      reveniumApi.getProduct(productId),
      reveniumApi.getMySubscriptions(productId),
    ])
      .then(([product, subs]) => {
        if (!active) return;
        const list = subs?._embedded?.subscriptionResourceList ?? subs ?? [];
        setState({
          loading: false,
          product,
          subscribed: (Array.isArray(list) ? list : []).length > 0,
        });
      })
      .catch(e => active && setState({ loading: false, error: String(e.message ?? e) }));
    return () => {
      active = false;
    };
  }, [reveniumApi, productId]);

  if (!sourceId) return null;

  return (
    <InfoCard title="Revenium">
      {state.loading && <Progress />}
      {state.error && <Typography color="error">{state.error}</Typography>}
      {!state.loading && !state.error && (
        <>
          {state.product ? (
            <>
              <Typography variant="body1">
                Product:{' '}
                <Link to={`/products/${productId}`}>
                  {state.product.name ?? productId}
                </Link>
              </Typography>
              <Chip
                label={state.subscribed ? 'Subscribed' : 'Not subscribed'}
                color={state.subscribed ? 'primary' : 'default'}
                size="small"
              />
              {!state.subscribed && (
                <Button
                  component={Link}
                  to={`/products/${productId}`}
                  variant="outlined"
                  size="small"
                  className={classes.requestAccessButton}
                >
                  Request access
                </Button>
              )}
            </>
          ) : (
            <Typography variant="body2">
              Metered by Revenium (source {sourceId}); no published product
              references this API.
            </Typography>
          )}
        </>
      )}
    </InfoCard>
  );
};
